import { useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Typography, Checkbox, FormControlLabel } from "@mui/material";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { usePlacesWidget } from "react-google-autocomplete";
import get from "lodash/get";

import { healthOrganisationSchema } from "../../yup";
import Input from "../../components/UserInput/Input";
import { useCreateHealthOrganisationMutation } from "../../services/api";
import style from "./heathOrganisation.module.css";
import { Button, LabelBox } from "../../components";
import { appConfig } from "../../hooks/useConfig";
import { colors } from "../../theme/colors";

type FormState = typeof healthOrganisationSchema.__outputType;

const AddHealthOrganisation = () => {
  const [createHealthOrganisation] = useCreateHealthOrganisationMutation();
  const navigation = useNavigate();

  const [message, setMessage] = useState<LabelMessageType | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(false);
  const [deliveryModeError, setDeliveryModeError] = useState(false);
  const [latitude, setLatitude] = useState(0);
  const [longitude, setLongitude] = useState(0);
  const [faxNumberSelected, setFaxNumberSelected] = useState(false);
  const [emailSelected, setEmailSelected] = useState(false);

  /*
   * useForm hook from react-hook-form
   * Manages form state, validation, and submission logic.
   * - register: Registers input fields and gathers their values.
   * - handleSubmit: Triggers the provided callback on form submission.
   * - formState: Provides access to form state, such as errors
   * - yupResolver(hospitalSchema): Restrict the type of payload data for required, min length etc
   */
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    clearErrors,
  } = useForm<FormState>({
    resolver: yupResolver(healthOrganisationSchema),
    mode: "onChange",
  });

  const { ref: materialRef } = usePlacesWidget({
    apiKey: appConfig?.GOOGLE_API_KEY,
    inputAutocompleteValue: "off",
    options: {
      types: [
        "street_address",
        "street_number",
        "intersection",
        "route",
        "health",
      ],
      componentRestrictions: { country: "au" },
    },
    onPlaceSelected: (place) => {
      let suburb = "";
      let state = "";
      let postal = "";
      let streetName = '';
      let streetNumber = '';
      if (place) {
        clearErrors("postcode");
        clearErrors("suburb");
        clearErrors("state");
        clearErrors('street_number')
        clearErrors('street_name');
        const address_comp = place?.address_components;
        if (address_comp) {
          for (const c of address_comp) {
            const types: Array<string> = c.types;
            if (
              !suburb &&
              (types.includes("locality") ||
                types.includes("sublocality") ||
                types.includes("sublocality_level_1") ||
                types.includes("postal_town") ||
                types.includes("administrative_area_level_3"))
            ) {
              suburb = c.short_name;
            }

            if (types.includes("administrative_area_level_1")) {
              state = c.short_name;
            } else if (types.includes("postal_code")) {
              postal = c.short_name;
            }
            if (types.includes('street_number')) {
              streetNumber = c.short_name;
            }
            if (types.includes('route')) {
              streetName = c.short_name;
            }
          }
          if (streetName === '' || streetNumber === '') {
            const match = place?.formatted_address?.match(/^(\d+)\s+(.+?),/);
            if (match) {
              streetNumber = match[1];
              streetName = match[2];
            } else {
              const address_arr = place?.formatted_address?.split(', ') ?? [];
              if (address_arr?.length > 3) {
                streetName = address_arr?.slice(0, 2).join(', ');
              } else {
                streetName = address_arr?.[0];
              }
            }
          }
          setValue("state", state);
          setValue("suburb", suburb);
          setValue("postcode", postal);
          setValue('street_number', streetNumber)
          setValue('street_name', streetName);
          const lat: any = get(place, "geometry.location.lat", () => { });
          const lng: any = get(place, "geometry.location.lng", () => { });
          setLatitude(lat() ?? 0);
          setLongitude(lng() ?? 0);
        }
      }
    },
  });

  const onSubmit = async (values: FormState) => {
    try {
      const {
        fax,
        mail,
        is_fax_delivery,
        is_mail_delivery,
        phone,
        ...rest
      } = values;
      if (mail === "" && fax === "") {
        setDeliveryModeError(true);
        return;
      }
      if (!faxNumberSelected && !emailSelected) {
        setError(true);
        return;
      }
      setIsLoading(true);
      const payload: AddHealthOrganisationPayload = {
        ...rest,
        phone:
          phone?.replace(/ /g, ""),
        fax: fax?.replace(/ /g, ""),
        mail: mail ?? "",
        is_fax_delivery: is_fax_delivery ?? false,
        is_mail_delivery: is_mail_delivery ?? false,
        latitude,
        longitude,
      };

      await createHealthOrganisation(payload).unwrap();
      setIsLoading(false);
      setTimeout(() => {
        navigation(-1);
      }, 1500);
    } catch (error: any) {
      setMessage({
        type: "error",
        text: error?.data?.detail?.toString() || "Please enter valid details",
      });
      setIsLoading(false);
    }
  };

  return (
    <Box className={style.container}>
      <Typography className={style.heading}>Add Health Organisation</Typography>
      <Typography className={style.description}>
        Enter the below details to start adding a new health organisation to Pegasus
        Cybersoft.
      </Typography>
      <Box component="form" onSubmit={handleSubmit(onSubmit)}>
        <Typography className={style.detailHeading2}>
          Health Organisation Details
        </Typography>
        <Box className={style.flexTop}>
          <Box className={style.row}>
            <Input
              placeholder="Enter name"
              label="Health Organisation Name"
              required
              register={register}
              name="name"
              errors={errors}
            />
            <Input
              placeholder="Enter health organisation provider name"
              label="Health Organisation Provider Number"
              required
              register={register}
              name="provider_number"
              errors={errors}
            />
          </Box>
          <Box className={style.row}>
            <Input
              placeholder="Enter unit number"
              label="Unit Number"
              register={register}
              name="unit_number"
              errors={errors}
            />
            <Input
              placeholder="Enter street number"
              label="Street Number"
              required
              register={register}
              name="street_number"
              errors={errors}
              inputRef={materialRef}
            />
          </Box>
          <Box className={style.row}>
            <Input
              placeholder="Enter street name"
              label="Street name"
              required
              register={register}
              name="street_name"
              errors={errors}
            />
            <Input
              placeholder="Enter suburb"
              label="Suburb"
              required
              register={register}
              name="suburb"
              errors={errors}
            />
          </Box>
          <Box className={style.row}>
            <Input
              placeholder="Enter state"
              label="State"
              required
              register={register}
              name="state"
              errors={errors}
            />
            <Input
              placeholder="Enter postcode"
              label="Postcode"
              required
              register={register}
              name="postcode"
              errors={errors}
            />
          </Box>
          <Typography className={style.detailHeading}>
            Contact Details
          </Typography>
          <Box className={style.flexBottom}>
            <Box className={style.row}>
              <Input
                placeholder="Enter phone number"
                label="Phone Number"
                required
                register={register}
                name="phone"
                errors={errors}
              />
              <Input
                placeholder="Enter accounts manager email address"
                label="Email Address (Accounts Manager)"
                required
                register={register}
                name="account_manager_mail"
                errors={errors}
              />
            </Box>
            <Typography className={style.detailHeading}>
              Request Form Delivery Details (Medical Records)
            </Typography>
            <Box className={style.row}>
              <Input
                placeholder="Enter email address"
                label="Email"
                register={register}
                name="mail"
                errors={errors}
                onFocus={() => {
                  setDeliveryModeError(false);
                }}
                onChange={(event) => {
                  setValue("mail", event.target.value);
                  if (event.target.value === "") {
                    setEmailSelected(false);
                  }
                }}
              />
              <Input
                placeholder="Enter fax number"
                label="Fax Number"
                register={register}
                name="fax"
                errors={errors}
                onFocus={() => {
                  setDeliveryModeError(false);
                }}
                onChange={(event) => {
                  setValue("fax", event.target.value, {
                    shouldValidate: true,
                  });
                  clearErrors("fax");
                  if (event.target.value === "") {
                    setFaxNumberSelected(false);
                  }
                }}
              />
            </Box>
            {deliveryModeError && (
              <Typography sx={{ color: colors.red, fontSize: "12px" }}>
                * Please provide either Email or Fax Number.
              </Typography>
            )}
            <Typography className={style.detailHeading}>
              Mode of Delivery
            </Typography>
            <Box sx={{ marginLeft: 1 }}>
              <FormControlLabel
                sx={{ "& span": { py: 0, px: 0.3 } }}
                control={
                  <Checkbox
                    {...register("is_mail_delivery")}
                    sx={{ color: error ? colors.red : colors.black }}
                    onChange={() => {
                      setError(false);
                      setEmailSelected(!emailSelected);
                    }}
                    checked={emailSelected}
                  />
                }
                label="Email"
                disabled={!watch("mail")}
              />
              <FormControlLabel
                sx={{ "& span": { py: 0, px: 0.3 } }}
                control={
                  <Checkbox
                    {...register("is_fax_delivery")}
                    sx={{ color: error ? colors.red : colors.black }}
                    onChange={() => {
                      setError(false);
                      setFaxNumberSelected(!faxNumberSelected);
                    }}
                    checked={faxNumberSelected}
                  />
                }
                label="Fax Number"
                disabled={!watch("fax")}
              />
            </Box>
            {error && (
              <Typography sx={{ color: colors.red, fontSize: "12px" }}>
                * Please select atleast one mode of delivery
              </Typography>
            )}
          </Box>
        </Box>
        {message && (
          <Box mt={2}>
            <LabelBox
              boxProps={{ height: 50, display: "flex", alignItems: "center" }}
              data={message}
              onClose={() => setMessage(null)}
            />
          </Box>
        )}
        <Box className={style.buttons}>
          <Button
            sx={{
              width: "200px",
              height: "55px",
              ":hover": { backgroundColor: "transparent" },
            }}
            variant="outlined"
            onClick={() => navigation(-1)}
            label={"Back"}
          />
          <Button
            sx={{
              width: "200px",
              height: "55px",
              ":hover": { backgroundColor: colors.primary },
            }}
            variant="contained"
            type="submit"
            label={"Save"}
            loading={isLoading}
          />
        </Box>
      </Box>
    </Box>
  );
}

export default AddHealthOrganisation;
